/**
 * Bestell-Draft (reine Logik, ohne I/O — daher gut testbar).
 *
 * Ein Draft ist die in Arbeit befindliche Bestellung am Gerät. Positionen gehen auf zwei
 * Wegen an den Server: beim Kassieren (applyPayment: nur die bezahlten Mengen) oder vorab per
 * „Senden" (sendLines: alle ungesendeten Positionen → lines.sent, Stationen drucken sofort).
 * Gesendete Zeilen sind gesperrt (nicht mehr änder- oder stornierbar) und werden nie erneut
 * gesendet.
 */

import {
  newId,
  lineTotalCents,
  lineRemainingQty,
  type Cents,
  type HlcClock,
  type OrderId,
  type OrderLineId,
  type VenueId,
  type TableId,
  type UserId,
  type DeviceId,
  type MenuItem,
  type SizeVariant,
  type OrderLine,
  type OrderLineModifier,
  type OrderPayment,
  type OrderEventBody,
  type EventEnvelope,
  type EventId,
  type PaymentId,
  type PaymentMethod,
} from "@orderman/shared";

export interface DraftLine {
  line: OrderLine;
  /** Wurde diese Zeile bereits als line.added an den Server geschickt? */
  sent: boolean;
  /** Wurde ein Storno (line.voided) für diese gesendete Zeile schon emittiert? */
  voidEmitted?: boolean;
}

export interface Draft {
  orderId: OrderId;
  venueId: VenueId;
  tableRef: TableId;
  waiterId: UserId;
  /** Wurde order.created schon gesendet? */
  createdSent: boolean;
  /** Mindestens einmal gesendet (für UI). */
  sentOnce: boolean;
  lines: DraftLine[];
  /** Erfasste (Teil-)Zahlungen / Belege. */
  payments: OrderPayment[];
}

export function createDraft(venueId: VenueId, tableRef: TableId, waiterId: UserId): Draft {
  return {
    orderId: newId() as OrderId,
    venueId,
    tableRef,
    waiterId,
    createdSent: false,
    sentOnce: false,
    lines: [],
    payments: [],
  };
}

/** Sicht auf den Draft für die Abrechnungs-Helfer aus @orderman/shared. */
export function payableOf(d: Draft): { lines: OrderLine[]; payments: OrderPayment[] } {
  return { lines: d.lines.map((dl) => dl.line), payments: d.payments };
}

/** Anzeigename + Einzelpreis einer Zeile inkl. gewählter Größe (Variante). */
function lineNameAndPrice(item: MenuItem, variant: SizeVariant | null): { name: string; unitPriceCents: Cents } {
  const base = item.shortLabel || item.name;
  return variant
    ? { name: `${base} ${variant.label}`, unitPriceCents: variant.priceCents }
    : { name: base, unitPriceCents: item.priceCents };
}

function sameLine(
  l: OrderLine,
  item: MenuItem,
  mods: OrderLineModifier[],
  note: string,
  variant: SizeVariant | null,
): boolean {
  if (l.menuItemId !== item.id) return false;
  if ((l.note ?? "") !== note) return false;
  // Verschiedene Größen (anderer Preis/Name) NICHT zusammenfassen.
  const np = lineNameAndPrice(item, variant);
  if (l.unitPriceCents !== np.unitPriceCents || l.name !== np.name) return false;
  const a = l.modifiers.map((m) => m.modifierId).sort();
  const b = mods.map((m) => m.modifierId).sort();
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Artikel hinzufügen: identische, noch nicht gesendete Zeile erhöht die Menge. */
export function addLine(
  d: Draft,
  item: MenuItem,
  mods: OrderLineModifier[] = [],
  note = "",
  variant: SizeVariant | null = null,
): Draft {
  const lines = d.lines.slice();
  const idx = lines.findIndex((dl) => !dl.sent && sameLine(dl.line, item, mods, note, variant));
  if (idx >= 0) {
    const dl = lines[idx]!;
    lines[idx] = { ...dl, line: { ...dl.line, qty: dl.line.qty + 1 } };
  } else {
    const np = lineNameAndPrice(item, variant);
    const line: OrderLine = {
      id: newId() as OrderLineId,
      menuItemId: item.id,
      name: np.name,
      qty: 1,
      unitPriceCents: np.unitPriceCents,
      taxRate: item.taxRate,
      modifiers: mods,
      status: "active",
      ...(note ? { note } : {}),
      ...(item.isInfo ? { isInfo: true } : {}),
    };
    lines.push({ line, sent: false });
  }
  return { ...d, lines };
}

/** Notiz auf einer noch nicht gesendeten Zeile ändern (gesendete bleiben unverändert). */
export function updateLineNote(d: Draft, lineId: OrderLineId, note: string): Draft {
  const lines = d.lines.map<DraftLine>((dl) => {
    if (dl.line.id !== lineId || dl.sent) return dl;
    const trimmed = note.trim();
    // exactOptionalPropertyTypes: Feld weglassen statt undefined setzen.
    const { note: _drop, ...rest } = dl.line;
    void _drop;
    const nextLine: OrderLine = trimmed ? { ...rest, note: trimmed } : rest;
    return { ...dl, line: nextLine };
  });
  return { ...d, lines };
}

/** Größe/Beilage/Extras + Notiz einer noch nicht gesendeten Zeile ändern (gesendete bleiben unverändert). */
export function updateLineOptions(
  d: Draft,
  lineId: OrderLineId,
  item: MenuItem,
  variant: SizeVariant | null,
  mods: OrderLineModifier[],
  note: string,
): Draft {
  const lines = d.lines.map<DraftLine>((dl) => {
    if (dl.line.id !== lineId || dl.sent) return dl;
    const trimmed = note.trim();
    const np = lineNameAndPrice(item, variant);
    // exactOptionalPropertyTypes: Feld weglassen statt undefined setzen.
    const { note: _drop, ...rest } = dl.line;
    void _drop;
    const nextLine: OrderLine = {
      ...rest,
      name: np.name,
      unitPriceCents: np.unitPriceCents,
      modifiers: mods,
      ...(trimmed ? { note: trimmed } : {}),
    };
    return { ...dl, line: nextLine };
  });
  return { ...d, lines };
}

/** Menge ändern. Sinkt eine NOCH NICHT gesendete Zeile auf 0, wird sie entfernt. */
export function changeQty(d: Draft, lineId: OrderLineId, delta: number): Draft {
  const lines = d.lines.flatMap<DraftLine>((dl) => {
    if (dl.line.id !== lineId) return [dl];
    if (dl.sent) return [dl]; // gesendet = gesperrt (Menge steht an der Station schon fest)
    const qty = dl.line.qty + delta;
    if (qty <= 0) return [];
    return [{ ...dl, line: { ...dl.line, qty } }];
  });
  return { ...d, lines };
}

/** Zeile entfernen/stornieren: ungesendet → löschen, gesendet → als voided markieren. */
export function voidLine(d: Draft, lineId: OrderLineId): Draft {
  const lines = d.lines.flatMap<DraftLine>((dl) => {
    if (dl.line.id !== lineId) return [dl];
    if (!dl.sent) return [];
    return [{ ...dl, line: { ...dl.line, status: "voided" } }];
  });
  return { ...d, lines };
}

export function draftTotalCents(d: Draft): Cents {
  return d.lines.reduce((s, dl) => s + lineTotalCents(dl.line), 0) as Cents;
}

/** Anzahl noch nicht gesendeter aktiver Zeilen (für den "Senden"-Button). */
export function unsentCount(d: Draft): number {
  return d.lines.filter((dl) => !dl.sent && dl.line.status === "active").length;
}

export interface EventDeps {
  mkEventId: () => EventId;
  clock: HlcClock;
  wallMs: number;
  deviceId: DeviceId;
}

/**
 * Erzeugt die Events für die noch nicht gesendeten Änderungen und liefert den
 * fortgeschriebenen Draft (Zeilen als gesendet/voided markiert). Reine Funktion.
 */
export function draftToEvents(d: Draft, deps: EventDeps): { events: EventEnvelope[]; nextDraft: Draft } {
  const events: EventEnvelope[] = [];
  const env = (body: OrderEventBody): EventEnvelope => ({
    id: deps.mkEventId(),
    venueId: d.venueId,
    deviceId: deps.deviceId,
    hlc: deps.clock.now(deps.wallMs),
    body,
  });

  let createdSent = d.createdSent;
  const ensureCreated = () => {
    if (!createdSent) {
      events.push(
        env({ kind: "order.created", orderId: d.orderId, tableRef: d.tableRef, waiterId: d.waiterId }),
      );
      createdSent = true;
    }
  };

  let hadNewLine = false;
  const lines = d.lines.map<DraftLine>((dl) => {
    let out = dl;
    if (!out.sent && out.line.status === "active") {
      ensureCreated();
      events.push(env({ kind: "line.added", orderId: d.orderId, line: out.line }));
      out = { ...out, sent: true };
      hadNewLine = true;
    }
    if (out.sent && out.line.status === "voided" && !out.voidEmitted) {
      ensureCreated();
      events.push(env({ kind: "line.voided", orderId: d.orderId, lineId: out.line.id }));
      out = { ...out, voidEmitted: true };
    }
    return out;
  });

  if (hadNewLine) events.push(env({ kind: "order.sent", orderId: d.orderId }));

  return {
    events,
    nextDraft: { ...d, createdSent, sentOnce: d.sentOnce || events.length > 0, lines },
  };
}

/**
 * „Senden" vor dem Kassieren: alle noch nicht gesendeten aktiven Positionen (inkl. Info-Zeilen)
 * als line.added an den Server + EIN lines.sent als Auslöser für die Stationen. Die Zeilen
 * bleiben unbezahlt (Kassieren später wie gewohnt), sind ab jetzt aber gesperrt. Reine Funktion.
 */
export function sendLines(d: Draft, deps: EventDeps): { events: EventEnvelope[]; nextDraft: Draft } {
  const events: EventEnvelope[] = [];
  const env = (body: OrderEventBody): EventEnvelope => ({
    id: deps.mkEventId(),
    venueId: d.venueId,
    deviceId: deps.deviceId,
    hlc: deps.clock.now(deps.wallMs),
    body,
  });

  const toSend = d.lines.filter((dl) => !dl.sent && dl.line.status === "active");
  if (toSend.length === 0) return { events, nextDraft: d };

  if (!d.createdSent) {
    events.push(env({ kind: "order.created", orderId: d.orderId, tableRef: d.tableRef, waiterId: d.waiterId }));
  }
  for (const dl of toSend) events.push(env({ kind: "line.added", orderId: d.orderId, line: dl.line }));
  events.push(
    env({ kind: "lines.sent", orderId: d.orderId, lineIds: toSend.map((dl) => dl.line.id), atMs: deps.wallMs }),
  );

  const ids = new Set(toSend.map((dl) => dl.line.id));
  const lines = d.lines.map<DraftLine>((dl) => (ids.has(dl.line.id) ? { ...dl, sent: true } : dl));
  return { events, nextDraft: { ...d, createdSent: true, sentOnce: true, lines } };
}

export interface PaymentDeps {
  mkEventId: () => EventId;
  mkLineId: () => OrderLineId;
  mkPaymentId: () => PaymentId;
  clock: HlcClock;
  wallMs: number;
  deviceId: DeviceId;
}

/**
 * Zahlung erfassen = Beleg erstellen. Nur die BEZAHLTEN Mengen werden gesendet
 * (order.created einmalig + line.added je bezahlter Zeile + payment.recorded).
 * Teil-Mengen werden gesplittet: die bezahlte Menge wird zu einer eigenen, gesperrten
 * Zeile, der unbezahlte Rest bleibt eine eigene, frei editierbare (unversandte) Zeile.
 * Vorab gesendete Zeilen (sendLines) werden nicht gesplittet: sie liegen schon am Server, die
 * Zahlung verweist nur auf Teilmengen. Invariante: gesendet = unveränderlich, ungesendet = editierbar.
 */
export function applyPayment(
  d: Draft,
  items: { lineId: OrderLineId; qty: number }[],
  method: PaymentMethod,
  deps: PaymentDeps,
): { events: EventEnvelope[]; nextDraft: Draft; payment: OrderPayment } {
  const events: EventEnvelope[] = [];
  const env = (body: OrderEventBody): EventEnvelope => ({
    id: deps.mkEventId(),
    venueId: d.venueId,
    deviceId: deps.deviceId,
    hlc: deps.clock.now(deps.wallMs),
    body,
  });
  let createdSent = d.createdSent;
  const ensureCreated = () => {
    if (!createdSent) {
      events.push(
        env({ kind: "order.created", orderId: d.orderId, tableRef: d.tableRef, waiterId: d.waiterId }),
      );
      createdSent = true;
    }
  };

  const lines = d.lines.slice();
  const paidItems: { lineId: OrderLineId; qty: number }[] = [];
  let amount = 0;

  for (const it of items) {
    const idx = lines.findIndex((dl) => dl.line.id === it.lineId);
    if (idx < 0) continue;
    const dl = lines[idx]!;
    if (dl.line.status === "voided") continue;
    const perUnit = dl.line.unitPriceCents + dl.line.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0);

    // Vorab gesendete Zeile: liegt schon am Server → nicht splitten/erneut senden, die Zahlung
    // verweist nur auf (Teil-)Mengen dieser Zeile.
    if (dl.sent) {
      const payQty = Math.min(it.qty, lineRemainingQty(payableOf(d), dl.line));
      if (payQty <= 0) continue;
      paidItems.push({ lineId: dl.line.id, qty: payQty });
      amount += perUnit * payQty;
      continue;
    }

    const payQty = Math.min(it.qty, dl.line.qty);
    if (payQty <= 0) continue;

    let paidLine: DraftLine;
    if (payQty === dl.line.qty) {
      paidLine = { line: dl.line, sent: true };
      lines[idx] = paidLine;
    } else {
      // splitten: Rest bleibt ungesendet, bezahlte Menge wird eigene Zeile
      lines[idx] = { line: { ...dl.line, qty: dl.line.qty - payQty }, sent: false };
      paidLine = { line: { ...dl.line, id: deps.mkLineId(), qty: payQty }, sent: true };
      lines.push(paidLine);
    }
    ensureCreated();
    events.push(env({ kind: "line.added", orderId: d.orderId, line: paidLine.line }));
    paidItems.push({ lineId: paidLine.line.id, qty: payQty });
    amount += perUnit * payQty;
  }

  const payment: OrderPayment = {
    paymentId: deps.mkPaymentId(),
    items: paidItems,
    amountCents: amount as Cents,
    tipCents: 0 as Cents,
    method,
    atMs: deps.wallMs,
  };
  events.push(
    env({
      kind: "payment.recorded",
      orderId: d.orderId,
      paymentId: payment.paymentId,
      items: paidItems,
      amountCents: payment.amountCents,
      tipCents: payment.tipCents,
      method,
      atMs: payment.atMs,
    }),
  );

  return {
    events,
    nextDraft: { ...d, createdSent, sentOnce: true, lines, payments: [...d.payments, payment] },
    payment,
  };
}
